import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { adminAPI, API_ORIGIN } from '../../../services/api';
import { FiSave, FiPlus, FiTrash2, FiArrowUp, FiArrowDown, FiEdit, FiHeadphones, FiBook, FiFileText, FiChevronDown, FiChevronUp } from 'react-icons/fi';
import GrammarBlockEditor from './blocks/GrammarBlockEditor';
import ListeningBlockEditor from './blocks/ListeningBlockEditor';
import ReadingBlockEditor from './blocks/ReadingBlockEditor';
import WritingBlockEditor from './blocks/WritingBlockEditor';
import '../../Admin/CreateLesson/CreateLessonBlocks.css';

const BLOCK_TYPES = [
    { value: 'grammar', label: 'Грамматика', icon: <FiEdit />, description: 'Упражнения по грамматике' },
    { value: 'listening', label: 'Аудирование', icon: <FiHeadphones />, description: 'Прослушивание и вопросы True/False/Not Given' },
    { value: 'reading', label: 'Чтение', icon: <FiBook />, description: 'Текст и вопросы с вариантами' },
    { value: 'writing', label: 'Письмо', icon: <FiFileText />, description: 'Письменное задание с проверкой преподавателем' }
];

function parseCorrectAnswerFromDb(raw) {
    if (raw == null || raw === '') return '';
    if (typeof raw !== 'string') return raw;
    const s = raw.trim();
    if (!s) return '';
    try {
        return JSON.parse(s);
    } catch {
        return s;
    }
}

const CreateTestPageBlocks = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEditMode = !!id;
    
    const [levels, setLevels] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    
    // Основные параметры теста
    const [testData, setTestData] = useState({
        title: '',
        description: '',
        difficulty: 'intermediate',
        levelId: '',
        timeLimit: 60,
        passingScore: 70
    });
    
    // Блоки теста
    const [blocks, setBlocks] = useState([]);
    
    // Состояние создания блока
    const [isAddingBlock, setIsAddingBlock] = useState(false);
    
    // Состояние сворачивания блоков
    const [collapsedBlocks, setCollapsedBlocks] = useState({});
    
    // Доступные голоса для TTS
    const [availableVoices, setAvailableVoices] = useState([]);

    useEffect(() => {
        loadInitialData();
        loadVoices();
    }, []);
    
    useEffect(() => {
        if (isEditMode && levels.length > 0) {
            loadTestForEdit();
        }
    }, [isEditMode, id, levels.length]);

    const loadInitialData = async () => {
        try {
            const levelsRes = await adminAPI.getLevels();
            setLevels(levelsRes.data.levels || []);
        } catch (err) {
            setError('Ошибка загрузки данных');
        }
    };

    const loadVoices = async () => {
        try {
            const response = await fetch(`${API_ORIGIN}/api/tts/voices`);
            const data = await response.json();
            const validVoices = (data.voices || []).filter(v => v.ShortName && v.DisplayName);
            setAvailableVoices(validVoices);
        } catch (error) {
            console.error('Ошибка загрузки голосов:', error);
            setAvailableVoices([]);
        }
    };

    const loadTestForEdit = async () => {
        try {
            setLoading(true);
            const response = await adminAPI.getTestBlocks(id);
            const test = response.data;
            
            setTestData({
                title: test.title || '',
                description: test.description || '',
                difficulty: test.difficulty || 'intermediate',
                levelId: test.level_id || '',
                timeLimit: test.time_limit || 60,
                passingScore: test.passing_score || 70
            });
            
            // Преобразуем блоки в формат для редактора
            const transformedBlocks = (test.blocks || []).map(block => {
                const transformed = {
                    id: block.id,
                    type: block.block_type,
                    title: block.title,
                    isNew: false
                };
                
                if (block.block_type === 'grammar') {
                    transformed.exercises = (block.exercises || []).map(ex => ({
                        id: ex.id,
                        title: ex.title || '',
                        type: ex.exercise_type || 'multiple_choice',
                        instructions: ex.instructions || '',
                        questions: (ex.questions || []).map(q => {
                            let questionData = {};
                            let correctAnswer = '';
                            
                            if (typeof q.question_data === 'string') {
                                try {
                                    questionData = q.question_data ? JSON.parse(q.question_data) : {};
                                } catch (e) {
                                    questionData = {};
                                }
                            } else {
                                questionData = q.question_data || {};
                            }
                            
                            if (typeof q.correct_answer === 'string') {
                                correctAnswer = parseCorrectAnswerFromDb(q.correct_answer);
                            } else {
                                correctAnswer = q.correct_answer;
                            }
                            
                            return {
                                id: q.id,
                                questionText: q.question_text || '',
                                questionData,
                                correctAnswer,
                                explanation: q.explanation || '',
                                points: q.points || 1
                            };
                        })
                    }));
                    
                } else if (block.block_type === 'listening') {
                    const listeningBlock = block.listeningBlock || {};
                    transformed.description = listeningBlock.description || '';
                    transformed.audioText = listeningBlock.audio_text || '';
                    transformed.audioUrl = listeningBlock.audio_url || '';
                    transformed.voiceId = listeningBlock.voice_id || 'en-US-JennyNeural';
                    transformed.questions = (block.questions || []).map(q => ({
                        id: q.id,
                        questionText: q.question_text || '',
                        correctAnswer: q.correct_answer || 'true',
                        explanation: q.explanation || '',
                        points: q.points || 1
                    }));
                    
                } else if (block.block_type === 'reading') {
                    const readingBlock = block.readingBlock || {};
                    transformed.readingText = readingBlock.reading_text || '';
                    transformed.questions = (block.questions || []).map(q => {
                        let options = [];
                        let allowMultipleCorrect = false;
                        if (typeof q.options === 'string') {
                            try {
                                const parsed = q.options ? JSON.parse(q.options) : [];
                                if (Array.isArray(parsed)) {
                                    options = parsed;
                                } else if (parsed && typeof parsed === 'object') {
                                    options = parsed.options || [];
                                    allowMultipleCorrect = !!parsed.allowMultipleCorrect;
                                }
                            } catch (e) {
                                options = [];
                            }
                        } else if (Array.isArray(q.options)) {
                            options = q.options;
                        } else if (q.options && typeof q.options === 'object') {
                            options = q.options.options || [];
                            allowMultipleCorrect = !!q.options.allowMultipleCorrect;
                        } else {
                            options = [];
                        }

                        let correctAnswer = q.correct_answer || '';
                        if (typeof correctAnswer === 'string') {
                            correctAnswer = parseCorrectAnswerFromDb(correctAnswer);
                        }
                        
                        return {
                            id: q.id,
                            questionText: q.question_text || '',
                            options,
                            allowMultipleCorrect,
                            correctAnswer,
                            explanation: q.explanation || '',
                            points: q.points || 1
                        };
                    });
                    
                } else if (block.block_type === 'writing') {
                    const writingBlock = block.writingBlock || {};
                    transformed.taskDescription = writingBlock.task_description || '';
                    transformed.minWords = writingBlock.min_words || 150;
                    transformed.maxWords = writingBlock.max_words || 300;
                    transformed.maxPoints = writingBlock.max_points || 20;
                }
                
                return transformed;
            });
            
            setBlocks(transformedBlocks);
            setLoading(false);
        } catch (error) {
            console.error('Ошибка загрузки теста:', error);
            setError('Ошибка загрузки теста');
            setLoading(false);
        }
    };

    const handleAddBlock = (type) => {
        const newBlock = {
            id: Date.now(),
            type,
            title: `${BLOCK_TYPES.find(t => t.value === type)?.label} ${blocks.length + 1}`,
            isNew: true
        };
        
        // Инициализируем данные в зависимости от типа блока
        if (type === 'grammar') {
            newBlock.exercises = [];
        } else if (type === 'listening') {
            newBlock.description = '';
            newBlock.audioText = '';
            newBlock.audioUrl = '';
            newBlock.voiceId = 'en-US-JennyNeural';
            newBlock.questions = [];
        } else if (type === 'reading') {
            newBlock.readingText = '';
            newBlock.questions = [];
        } else if (type === 'writing') {
            newBlock.taskDescription = '';
            newBlock.minWords = 150;
            newBlock.maxWords = 300;
            newBlock.maxPoints = 20;
        }
        
        setBlocks([...blocks, newBlock]);
        setIsAddingBlock(false);
    };

    const handleUpdateBlock = (index, updatedBlock) => {
        const updated = [...blocks];
        updated[index] = { ...updated[index], ...updatedBlock };
        console.log('Block updated:', index, updatedBlock); // Отладка
        console.log('Full block after update:', updated[index]); // Отладка
        setBlocks(updated);
    };

    const handleRemoveBlock = (index) => {
        if (window.confirm('Вы уверены, что хотите удалить этот блок?')) {
            setBlocks(blocks.filter((_, i) => i !== index));
        }
    };

    const handleMoveBlock = (index, direction) => {
        if (direction === 'up' && index === 0) return;
        if (direction === 'down' && index === blocks.length - 1) return;
        
        const newIndex = direction === 'up' ? index - 1 : index + 1;
        const updated = [...blocks];
        [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
        setBlocks(updated);
    };
    
    const toggleBlockCollapse = (index) => {
        setCollapsedBlocks(prev => ({
            ...prev,
            [index]: !prev[index]
        }));
    };

    const handleSaveTest = async () => {
        try {
            setLoading(true);
            setError('');
            
            if (!testData.title.trim()) {
                setError('Введите название теста');
                setLoading(false);
                return;
            }
            
            const dataToSend = {
                ...testData,
                blocks
            };
            
            console.log('=== SAVING TEST ==='); // Отладка
            console.log('Blocks to save:', blocks); // Отладка
            blocks.forEach((block, index) => {
                console.log(`Block ${index} (${block.type}):`, {
                    title: block.title,
                    questionsCount: block.questions?.length || 0,
                    questions: block.questions
                });
            });
            
            if (isEditMode) {
                await adminAPI.updateTestBlocks(id, dataToSend);
                setSuccess('Тест успешно обновлен!');
            } else {
                await adminAPI.createTestBlocks(dataToSend);
                setSuccess('Тест успешно создан!');
            }
            
            setTimeout(() => {
                navigate('/admin/tests');
            }, 1500);
            
        } catch (err) {
            console.error('Ошибка сохранения теста:', err);
            setError(err.response?.data?.error || 'Ошибка сохранения теста');
            setLoading(false);
        }
    };

    const renderBlockEditor = (block, index) => {
        const commonProps = {
            block,
            onUpdate: (updatedBlock) => handleUpdateBlock(index, updatedBlock),
            availableVoices,
            setError,
            setSuccess
        };

        switch (block.type) {
            case 'grammar':
                return <GrammarBlockEditor {...commonProps} />;
            case 'listening':
                return <ListeningBlockEditor {...commonProps} />;
            case 'reading':
                return <ReadingBlockEditor {...commonProps} />;
            case 'writing':
                return <WritingBlockEditor {...commonProps} />;
            default:
                return null;
        }
    };

    if (loading && isEditMode) {
        return (
            <div className="container" style={{ padding: '40px' }}>
                <div className="loading">Загрузка теста...</div>
            </div>
        );
    }

    return (
        <div className="container" style={{ padding: '40px', maxWidth: '1400px', margin: '0 auto' }}>
            <div className="page-header" style={{ marginBottom: '30px' }}>
                <h1>{isEditMode ? 'Редактировать тест' : 'Создать тест'}</h1>
                <p>Создайте тест из различных блоков</p>
            </div>

            {error && (
                <div className="alert alert-error" style={{ marginBottom: '20px', padding: '15px', background: '#fee', border: '1px solid #fcc', borderRadius: '8px', color: '#c33' }}>
                    {error}
                </div>
            )}
            
            {success && (
                <div className="alert alert-success" style={{ marginBottom: '20px', padding: '15px', background: '#efe', border: '1px solid #cfc', borderRadius: '8px', color: '#3c3' }}>
                    {success}
                </div>
            )}

            {/* Основные параметры теста */}
            <div className="card" style={{ marginBottom: '30px', padding: '25px', background: 'white', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                <h2 style={{ marginBottom: '20px' }}>Основные настройки</h2>
                
                <div style={{ display: 'grid', gap: '20px' }}>
                    <div>
                        <label className="form-label" style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>Название теста *</label>
                        <input 
                            type="text"
                            className="form-input"
                            style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px' }}
                            placeholder="Например: Итоговый тест по Present Perfect"
                            value={testData.title}
                            onChange={(e) => setTestData({ ...testData, title: e.target.value })}
                        />
                    </div>

                    <div>
                        <label className="form-label" style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>Описание</label>
                        <textarea 
                            className="form-input"
                            style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px' }}
                            placeholder="Краткое описание теста..."
                            value={testData.description}
                            onChange={(e) => setTestData({ ...testData, description: e.target.value })}
                            rows={3}
                        />
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '15px' }}>
                        <div>
                            <label className="form-label" style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>Сложность</label>
                            <select 
                                className="form-input"
                                style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px' }}
                                value={testData.difficulty}
                                onChange={(e) => setTestData({ ...testData, difficulty: e.target.value })}
                            >
                                <option value="beginner">Beginner</option>
                                <option value="elementary">Elementary</option>
                                <option value="pre-intermediate">Pre-Intermediate</option>
                                <option value="intermediate">Intermediate</option>
                                <option value="upper-intermediate">Upper-Intermediate</option>
                                <option value="advanced">Advanced</option>
                            </select>
                        </div>

                        <div>
                            <label className="form-label" style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>Уровень</label>
                            <select 
                                className="form-input"
                                style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px' }}
                                value={testData.levelId}
                                onChange={(e) => setTestData({ ...testData, levelId: e.target.value })}
                            >
                                <option value="">Не выбрано</option>
                                {levels.map(level => (
                                    <option key={level.id} value={level.id}>{level.name}</option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label className="form-label" style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>Время на тест (минуты)</label>
                            <input 
                                type="number"
                                className="form-input"
                                style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px' }}
                                value={testData.timeLimit}
                                onChange={(e) => setTestData({ ...testData, timeLimit: parseInt(e.target.value) || 0 })}
                                min="0"
                                placeholder="0 - без ограничения"
                            />
                        </div>

                        <div>
                            <label className="form-label" style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>Проходной балл (%)</label>
                            <input 
                                type="number"
                                className="form-input"
                                style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px' }}
                                value={testData.passingScore}
                                onChange={(e) => setTestData({ ...testData, passingScore: parseInt(e.target.value) || 70 })}
                                min="0"
                                max="100"
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Блоки теста */}
            <div style={{ marginBottom: '30px' }}>
                <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    marginBottom: '20px' 
                }}>
                    <h2>Блоки теста ({blocks.length})</h2>
                    <div style={{ display: 'flex', gap: '10px' }}>
                        {blocks.length > 0 && (
                            <button 
                                className="btn btn-secondary"
                                onClick={() => {
                                    const allCollapsed = blocks.every((_, i) => collapsedBlocks[i]);
                                    const newState = {};
                                    blocks.forEach((_, i) => {
                                        newState[i] = !allCollapsed;
                                    });
                                    setCollapsedBlocks(newState);
                                }}
                                style={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: '8px',
                                    padding: '10px 16px',
                                    background: '#f0f0f0',
                                    border: 'none',
                                    borderRadius: '6px',
                                    cursor: 'pointer'
                                }}
                            >
                                {blocks.every((_, i) => collapsedBlocks[i]) ? (
                                    <>
                                        <FiChevronDown /> Развернуть все
                                    </>
                                ) : (
                                    <>
                                        <FiChevronUp /> Свернуть все
                                    </>
                                )}
                            </button>
                        )}
                        <button 
                            className="btn btn-primary"
                            onClick={() => setIsAddingBlock(!isAddingBlock)}
                            style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: '8px',
                                padding: '10px 16px',
                                background: '#4a90e2',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontWeight: '600'
                            }}
                        >
                            <FiPlus /> Добавить блок
                        </button>
                    </div>
                </div>

                {isAddingBlock && (
                    <div className="card" style={{ marginBottom: '20px', background: '#f8f9fa', padding: '25px', borderRadius: '8px' }}>
                        <h3 style={{ marginBottom: '15px' }}>Выберите тип блока</h3>
                        <div style={{ 
                            display: 'grid', 
                            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                            gap: '15px' 
                        }}>
                            {BLOCK_TYPES.map(blockType => (
                                <div
                                    key={blockType.value}
                                    onClick={() => handleAddBlock(blockType.value)}
                                    style={{
                                        padding: '20px',
                                        background: 'white',
                                        border: '2px solid #e0e0e0',
                                        borderRadius: '8px',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s',
                                        textAlign: 'center'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.borderColor = '#4a90e2';
                                        e.currentTarget.style.transform = 'translateY(-2px)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.borderColor = '#e0e0e0';
                                        e.currentTarget.style.transform = 'translateY(0)';
                                    }}
                                >
                                    <div style={{ fontSize: '32px', marginBottom: '10px', color: '#4a90e2' }}>
                                        {blockType.icon}
                                    </div>
                                    <div style={{ fontWeight: '600', marginBottom: '5px' }}>
                                        {blockType.label}
                                    </div>
                                    <div style={{ fontSize: '12px', color: '#666' }}>
                                        {blockType.description}
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div style={{ marginTop: '15px', textAlign: 'right' }}>
                            <button 
                                className="btn btn-secondary"
                                onClick={() => setIsAddingBlock(false)}
                                style={{
                                    padding: '8px 16px',
                                    background: '#f0f0f0',
                                    border: 'none',
                                    borderRadius: '6px',
                                    cursor: 'pointer'
                                }}
                            >
                                Отмена
                            </button>
                        </div>
                    </div>
                )}

                {blocks.length === 0 && !isAddingBlock && (
                    <div className="card" style={{ textAlign: 'center', padding: '60px 20px', color: '#999', background: 'white', borderRadius: '8px' }}>
                        <FiFileText size={48} style={{ marginBottom: '20px', opacity: 0.5 }} />
                        <p>Пока нет блоков. Нажмите "Добавить блок" чтобы начать</p>
                    </div>
                )}

                {blocks.map((block, index) => {
                    const isCollapsed = collapsedBlocks[index];
                    
                    return (
                    <div key={block.id || index} className="card" style={{ marginBottom: '20px', position: 'relative', padding: '20px', background: 'white', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                        <div style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center',
                            marginBottom: isCollapsed ? '0' : '20px',
                            paddingBottom: isCollapsed ? '0' : '15px',
                            borderBottom: isCollapsed ? 'none' : '2px solid #e8e8e8'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                                <div style={{ fontSize: '24px', color: '#4a90e2' }}>
                                    {BLOCK_TYPES.find(t => t.value === block.type)?.icon}
                                </div>
                                <div style={{ flex: 1 }}>
                                    <input 
                                        type="text"
                                        className="form-input"
                                        placeholder="Название блока"
                                        value={block.title}
                                        onChange={(e) => handleUpdateBlock(index, { title: e.target.value })}
                                        style={{ 
                                            fontSize: '16px', 
                                            fontWeight: '600',
                                            maxWidth: '400px',
                                            padding: '6px 12px',
                                            border: '1px solid #ddd',
                                            borderRadius: '6px'
                                        }}
                                    />
                                    <div style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>
                                        {BLOCK_TYPES.find(t => t.value === block.type)?.label}
                                    </div>
                                </div>
                            </div>
                            
                            <div style={{ display: 'flex', gap: '8px' }}>
                                <button 
                                    onClick={() => toggleBlockCollapse(index)}
                                    title={isCollapsed ? 'Развернуть' : 'Свернуть'}
                                    style={{
                                        background: isCollapsed ? '#f0f0f0' : 'white',
                                        border: '1px solid #d9d9d9',
                                        borderRadius: '6px',
                                        padding: '8px 12px',
                                        cursor: 'pointer'
                                    }}
                                >
                                    {isCollapsed ? <FiChevronDown /> : <FiChevronUp />}
                                </button>
                                <button 
                                    onClick={() => handleMoveBlock(index, 'up')}
                                    disabled={index === 0}
                                    title="Переместить вверх"
                                    style={{
                                        background: 'white',
                                        border: '1px solid #d9d9d9',
                                        borderRadius: '6px',
                                        padding: '8px 12px',
                                        cursor: index === 0 ? 'not-allowed' : 'pointer',
                                        opacity: index === 0 ? 0.5 : 1
                                    }}
                                >
                                    <FiArrowUp />
                                </button>
                                <button 
                                    onClick={() => handleMoveBlock(index, 'down')}
                                    disabled={index === blocks.length - 1}
                                    title="Переместить вниз"
                                    style={{
                                        background: 'white',
                                        border: '1px solid #d9d9d9',
                                        borderRadius: '6px',
                                        padding: '8px 12px',
                                        cursor: index === blocks.length - 1 ? 'not-allowed' : 'pointer',
                                        opacity: index === blocks.length - 1 ? 0.5 : 1
                                    }}
                                >
                                    <FiArrowDown />
                                </button>
                                <button 
                                    onClick={() => handleRemoveBlock(index)}
                                    title="Удалить блок"
                                    style={{
                                        background: '#ff4d4f',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '6px',
                                        padding: '8px 12px',
                                        cursor: 'pointer'
                                    }}
                                >
                                    <FiTrash2 />
                                </button>
                            </div>
                        </div>

                        {!isCollapsed && renderBlockEditor(block, index)}
                    </div>
                    );
                })}
            </div>

            {/* Кнопки сохранения */}
            <div style={{ 
                display: 'flex', 
                gap: '15px', 
                justifyContent: 'flex-end',
                position: 'sticky',
                bottom: '20px',
                background: 'white',
                padding: '20px',
                borderRadius: '8px',
                boxShadow: '0 -2px 10px rgba(0,0,0,0.1)'
            }}>
                <button 
                    onClick={() => navigate('/admin/tests')}
                    disabled={loading}
                    style={{
                        padding: '12px 24px',
                        background: '#f0f0f0',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: loading ? 'not-allowed' : 'pointer',
                        fontWeight: '600'
                    }}
                >
                    Отмена
                </button>
                <button 
                    onClick={handleSaveTest}
                    disabled={loading || !testData.title}
                    style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '8px',
                        padding: '12px 24px',
                        background: '#52c41a',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: (loading || !testData.title) ? 'not-allowed' : 'pointer',
                        fontWeight: '600',
                        opacity: (loading || !testData.title) ? 0.6 : 1
                    }}
                >
                    <FiSave /> {loading ? 'Сохранение...' : 'Сохранить тест'}
                </button>
            </div>
        </div>
    );
};

export default CreateTestPageBlocks;
